import React from 'react';
import { useOnboarding, OnboardingStep } from '../../contexts/OnboardingContext';
import { Box, Paper, Typography, Button, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// ADDED: Define props for theme colors
interface OnboardingGuideProps {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
}

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({ primaryColor, backgroundColor, textColor }) => {
  const { isOnboarding, currentStep, nextStep, skipOnboarding, isAdminUser } = useOnboarding();

  if (!isOnboarding) {
    return null;
  }

  const getStepTitle = (): string => {
    switch (currentStep) {
      case OnboardingStep.BusinessSettings:
        return "Setup Your Business";
      case OnboardingStep.Roles:
        return "Create User Roles";
      case OnboardingStep.Users:
        return "Add Team Members";
      case OnboardingStep.AppointmentTypes:
        return "Configure Appointment Types";
      case OnboardingStep.Calendar:
        return "Create Your First Appointment";
      default:
        return "";
    }
  };

  const getStepDescription = (): string => {
    switch (currentStep) {
      case OnboardingStep.BusinessSettings:
        return "Set your time zone, business hours, and customize your theme colors.";
      case OnboardingStep.Roles:
        return "Define roles with specific permissions for your team members.";
      case OnboardingStep.Users:
        return "Invite team members and assign appropriate roles.";
      case OnboardingStep.AppointmentTypes:
        return "Create different types of appointments you'll offer to clients.";
      case OnboardingStep.Calendar:
        return "Schedule your first appointment to get started.";
      default:
        return "";
    }
  };

  // Calculate total steps and current step number based on actual flow
  // If user is on AppointmentTypes or Calendar only (business setup already done),
  // show as 2 steps total, otherwise show all 5 admin steps
  const isPostBusinessSetup = currentStep === OnboardingStep.AppointmentTypes || 
                               currentStep === OnboardingStep.Calendar;
  
  // Determine total steps and current step number
  const getStepInfo = (): { totalSteps: number; currentStepNumber: number } => {
    if (isPostBusinessSetup) {
      // User completed business setup, only showing appointment types and calendar
      return {
        totalSteps: 2,
        currentStepNumber: currentStep === OnboardingStep.AppointmentTypes ? 1 : 2
      };
    } else if (isAdminUser) {
      // Full admin onboarding flow
      const stepMap: Record<OnboardingStep, number> = {
        [OnboardingStep.BusinessSettings]: 1,
        [OnboardingStep.Roles]: 2,
        [OnboardingStep.Users]: 3,
        [OnboardingStep.AppointmentTypes]: 4,
        [OnboardingStep.Calendar]: 5,
        [OnboardingStep.Completed]: 5
      };
      return {
        totalSteps: 5,
        currentStepNumber: stepMap[currentStep] || 1
      };
    } else {
      // Non-admin user - should only see AppointmentTypes and Calendar
      return {
        totalSteps: 2,
        currentStepNumber: 1 // Default fallback
      };
    }
  };

  const { totalSteps, currentStepNumber } = getStepInfo();
  const isLastStep = currentStepNumber === totalSteps;
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        my: 4,
        borderTop: `4px solid ${primaryColor}`,
        bgcolor: backgroundColor,
        color: textColor,
        position: 'fixed',
        bottom: 150,
        right: 16,
        zIndex: 10,
        width: '30rem',
        maxWidth: 'calc(100% - 32px)',
        mx: 'auto'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: textColor }}>
          Step {currentStepNumber} of {totalSteps}
        </Typography>
        <Button 
          startIcon={<CloseIcon />} 
          onClick={skipOnboarding} 
          color="inherit"
          sx={{ mt: -1, color: textColor }}
        >
          Skip
        </Button>
      </Box>

      <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: textColor }}>
        {getStepTitle()}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: textColor }}>
        {getStepDescription()}
      </Typography>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          variant="contained"
          sx={{
            bgcolor: primaryColor,
            '&:hover': {
              bgcolor: primaryColor,
              filter: 'brightness(0.9)',
            }
          }}
          endIcon={<ChevronRightIcon />}
          onClick={nextStep}
        >
          {isLastStep ? 'Complete Setup' : 'Next Step'}
        </Button>
      </Stack>
    </Paper>
  );
};

export default OnboardingGuide; 